import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Stack,
  Paper,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
// Fancy skeletons replace default MUI Skeletons for a premium look
import { FancySkeleton } from '../common/FancySkeleton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LaunchIcon from '@mui/icons-material/Launch';
import CardTitle from '../common/CardTitle';
import ReputationPill from '../common/ReputationPill';
import IdeaDraftCard from './IdeaDraftCard';
import { logger } from '../../utils/logger';
import { IdeaData } from '../../hooks/useIdeaResults';

interface IdeaResultsContainerProps {
  ideaData: IdeaData;
  ideaId: string | undefined;
  onCopySuccess: (message: string) => void;
  onCopyError: (message: string) => void;
}

const decodeHtmlEntities = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }

  try {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = value;
      return textarea.value;
    }
  } catch (error) {
    logger.warn('Failed to decode HTML entities in sector label', error);
  }

  // Basic fallback handles the most common entity we expect
  return value.replace(/&amp;/g, '&');
};

const IdeaResultsContainer: React.FC<IdeaResultsContainerProps> = ({
  ideaData,
  ideaId,
  onCopySuccess,
  onCopyError
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [icpExpanded, setIcpExpanded] = useState(false);
  const [expandedMetrics, setExpandedMetrics] = useState<{ [key: string]: boolean }>({});
  const [expandedDetails, setExpandedDetails] = useState<{ [key: string]: boolean }>({});
  const [expandedSimulations, setExpandedSimulations] = useState<{ [key: string]: boolean }>({});
  const decodedSector = useMemo(() => decodeHtmlEntities(ideaData.sector), [ideaData.sector]);

  // Anchor scroll once when focusDraft query param is present
  const hasScrolledRef = useRef(false);
  useEffect(() => {
    if (hasScrolledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const focusDraft = params.get('focusDraft');
    if (!focusDraft) return;
    const el = document.getElementById(`draft-${focusDraft}`);
    if (el) {
      hasScrolledRef.current = true;
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('highlight-draft');
        setTimeout(() => el.classList.remove('highlight-draft'), 2000);
      }, 300);
    }
  }, [ideaData]);

  // Helper functions
  const getImpactColor = (level: string) => {
    switch (level) {
      case 'Strong Uplift': return '#06D6A0';
      case 'Mild Uplift': return '#80D6A0';
      case 'Neutral': return '#FFD166';
      case 'Mild Damage': return '#FF9A66';
      case 'Severe Damage': return '#FF6B6B';
      default: return '#9CA3AF';
    }
  };

  const getCreatorName = () => {
    if (!ideaData.user) return 'Unknown';
    if (ideaData.user.firstName && ideaData.user.lastName) {
      return `${ideaData.user.firstName} ${ideaData.user.lastName}`;
    }
    return ideaData.user.firstName || ideaData.user.lastName || ideaData.user.email || 'Unknown';
  };

  const getBaselineDeltaWord = (delta: string) => {
    if (!delta || delta === 'N/A') return 'N/A';
    
    const deltaLower = delta.toLowerCase();
    if (deltaLower.includes('exceeds')) return 'Exceeds';
    if (deltaLower.includes('aligns')) return 'Aligns';
    if (deltaLower.includes('violates')) return 'Violates';
    
    // Fallback to first word if none of the expected words are found
    return delta.split(' ')[0] || 'N/A';
  };

  const getInitialSimulationLimit = () => {
    if (isMobile) return 2;
    if (isTablet) return 3;
    return 5;
  };

  const handleCopyDraft = async (text: string, draftId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onCopySuccess('Copied to clipboard!');
    } catch (err) {
      logger.error('Failed to copy text: ', err);
      onCopyError('Failed to copy text to clipboard');
    }
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {ideaData.status === 'processing'
            ? 'Generating Your Ideas'
            : ideaData.status === 'queued'
            ? 'Ideas Queued'
            : 'Idea Details'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {ideaData.status === 'processing'
            ? `We're generating content ideas for ${ideaData.brand}. You can browse elsewhere; results will appear here when ready.`
            : ideaData.status === 'queued'
            ? `Your idea generation request is queued and will begin shortly.`
            : `Content ideas generated for ${ideaData.brand} on ${ideaData.platform}`}
        </Typography>
      </Box>

      <Stack spacing={4}>
        {/* Idea Overview */}
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <CardTitle 
            title="Idea Overview" 
            tooltip="Basic information about this content idea."
          />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Topic
              </Typography>
              <Box sx={{ 
                maxHeight: '120px',
                overflowY: 'auto',
                mb: 2,
                pr: 1, // Padding for scrollbar
                // Custom scrollbar styling
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                  borderRadius: '3px',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                  }
                }
              }}>
                <Typography variant="h6">
                  {ideaData.topic}
                </Typography>
              </Box>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Brand/Account
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {ideaData.brand}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Created By
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {getCreatorName()}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Platform
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {ideaData.platform}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Sector
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {decodedSector}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Created Date
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {new Date(ideaData.createdAt).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* ICP Details */}
        <Accordion 
          expanded={icpExpanded} 
          onChange={() => setIcpExpanded(!icpExpanded)}
          sx={{ 
            '&:before': { display: 'none' },
            boxShadow: 'none',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              bgcolor: 'background.paper',
              '&.Mui-expanded': { minHeight: 48 },
              '& .MuiAccordionSummary-content': { margin: '12px 0' }
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Audience Persona
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7A8D', fontSize: '14px' }}>
                {ideaData.icp.title}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3, lineHeight: 1.4 }}>
              {ideaData.icp.data}
            </Typography>
            
            {/* Audience Baseline */}
            {ideaData.audienceBaseline && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Audience Baseline Analysis
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', lineHeight: 1.4 }}>
                  {ideaData.audienceBaseline}
                </Typography>
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Content Drafts - processing placeholder or drafts list */}
        { (ideaData.status === 'queued' || ideaData.status === 'processing') ? (
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Idea Content</Typography>
            {/* Metrics chips row */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <FancySkeleton height={24} width={100} radius={12} />
              <FancySkeleton height={24} width={120} radius={12} />
              <FancySkeleton height={24} width={90} radius={12} />
              <FancySkeleton height={24} width={110} radius={12} />
              <FancySkeleton height={24} width={120} radius={12} />
            </Stack>
            {/* Draft text box */}
            <Box sx={{ p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'dark' ? '#111827' : '#F8F9FA', mb: 2 }}>
              <Stack spacing={1.2}>
                <FancySkeleton height={12} width="92%" radius={8} />
                <FancySkeleton height={12} width="88%" radius={8} />
                <FancySkeleton height={12} width="80%" radius={8} />
              </Stack>
            </Box>
            {/* Media Concept */}
            <Box sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'dark' ? '#0f1720' : '#E7F1FB', mb: 1.5 }}>
              <FancySkeleton height={14} width={150} radius={8} />
              <Box sx={{ mt: 1 }}>
                <FancySkeleton height={12} width="86%" radius={8} />
              </Box>
            </Box>
            {/* Strategy */}
            <Box sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'dark' ? '#0f1720' : '#E7F1FB', mb: 1.5 }}>
              <FancySkeleton height={14} width={110} radius={8} />
              <Stack spacing={1} sx={{ mt: 1 }}>
                <FancySkeleton height={12} width="92%" radius={8} />
                <FancySkeleton height={12} width="78%" radius={8} />
              </Stack>
            </Box>
            {/* Additional Recommendations */}
            <Box sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#FFF3CD', mb: 2 }}>
              <FancySkeleton height={14} width={190} radius={8} />
              <Box sx={{ mt: 1 }}>
                <FancySkeleton height={12} width="85%" radius={8} />
              </Box>
            </Box>
            {/* Additional Analytics grid */}
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Additional Analytics</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {Array.from({ length: 4 }).map((_, idx) => (
                <Grid item xs={6} md={3} key={`top-${idx}`}>
                  <Box sx={{ p: 2, textAlign: 'center', borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'dark' ? '#111827' : '#F8F9FA' }}>
                    <FancySkeleton height={12} width={120} radius={8} />
                    <Box sx={{ mt: 1 }}>
                      <FancySkeleton height={14} width={60} radius={8} />
                    </Box>
                  </Box>
                </Grid>
              ))}
              {Array.from({ length: 3 }).map((_, idx) => (
                <Grid item xs={6} md={3} key={`bottom-${idx}`}>
                  <Box sx={{ p: 2, textAlign: 'center', borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: (t) => t.palette.mode === 'dark' ? '#111827' : '#F8F9FA' }}>
                    <FancySkeleton height={12} width={120} radius={8} />
                    <Box sx={{ mt: 1 }}>
                      <FancySkeleton height={14} width={60} radius={8} />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            {/* End skeleton draft */}
          </Paper>
        ) : isMobile ? (
          // Mobile: No Paper wrapper to maximize width
          <>
            <Box sx={{ mb: 2 }}>
              <CardTitle 
                title="Content Ideas"
                tooltip="AI-generated content variations for this idea with projected impact analysis."
              />
            </Box>
            
            {ideaData.drafts.length === 0 ? (
              <Box sx={{ mt: 4 }}>
                <Typography variant="body1" color="text.secondary" align="center">
                  No drafts have been generated for this idea yet.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                mt: 2,
                scrollSnapType: 'y mandatory',
                overflowY: 'auto'
              }}>
                <Stack spacing={3}>
                {ideaData.drafts
                  .map((draft) => {
                    // Parse brandPulseJson to get complete data
                    let pulseData: any = {};
                    if (draft.brandPulseJson) {
                      try {
                        pulseData = JSON.parse(draft.brandPulseJson);
                      } catch (e) {
                        logger.error('Error parsing brandPulseJson:', e);
                      }
                    }
                    return { ...draft, pulseData };
                  })
                  .sort((a, b) => {
                    // Sort by UI score (highest first)
                    const scoreA = Math.round(a.pulseData.reputation?.uiScore || a.reputation?.uiScore || a.pulseData.score || a.score || a.projectedImpactScore || 0);
                    const scoreB = Math.round(b.pulseData.reputation?.uiScore || b.reputation?.uiScore || b.pulseData.score || b.score || b.projectedImpactScore || 0);
                    return scoreB - scoreA;
                  })
                  .map((draft, index) => (
                    <IdeaDraftCard
                      key={draft.id}
                      draft={draft}
                      index={index}
                      ideaData={ideaData}
                      ideaId={ideaId}
                      expandedMetrics={expandedMetrics}
                      expandedDetails={expandedDetails}
                      expandedSimulations={expandedSimulations}
                      onToggleMetrics={(draftId) => setExpandedMetrics(prev => ({ ...prev, [draftId]: !expandedMetrics[draftId] }))}
                      onToggleDetails={(draftId) => setExpandedDetails(prev => ({ ...prev, [draftId]: !expandedDetails[draftId] }))}
                      onToggleSimulations={(draftId) => setExpandedSimulations(prev => ({ ...prev, [draftId]: !expandedSimulations[draftId] }))}
                      onCopyDraft={handleCopyDraft}
                      getImpactColor={getImpactColor}
                      getBaselineDeltaWord={getBaselineDeltaWord}
                      getInitialSimulationLimit={getInitialSimulationLimit}
                    />
                  ))}
              </Stack>
            </Box>
          )}
        </>
        ) : (
          // Desktop: Keep Paper wrapper for consistency
          <Paper sx={{ p: 3 }}>
            <CardTitle 
              title="Content Ideas"
              tooltip="AI-generated content variations for this idea with projected impact analysis."
            />
            
            {ideaData.drafts.length === 0 ? (
              <Box sx={{ mt: 4 }}>
                <Typography variant="body1" color="text.secondary" align="center">
                  No drafts have been generated for this idea yet.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 4 }}>
                <Stack spacing={4}>
                  {ideaData.drafts
                    .map((draft) => {
                      // Parse brandPulseJson to get complete data
                      let pulseData: any = {};
                      if (draft.brandPulseJson) {
                        try {
                          pulseData = JSON.parse(draft.brandPulseJson);
                        } catch (e) {
                          logger.error('Error parsing brandPulseJson:', e);
                        }
                      }
                      return { ...draft, pulseData };
                    })
                    .sort((a, b) => {
                      // Sort by UI score (highest first)
                      const scoreA = Math.round(a.pulseData.reputation?.uiScore || a.reputation?.uiScore || a.pulseData.score || a.score || a.projectedImpactScore || 0);
                      const scoreB = Math.round(b.pulseData.reputation?.uiScore || b.reputation?.uiScore || b.pulseData.score || b.score || b.projectedImpactScore || 0);
                      return scoreB - scoreA;
                    })
                    .map((draft, index) => (
                      <IdeaDraftCard
                        key={draft.id}
                        draft={draft}
                        index={index}
                        ideaData={ideaData}
                        ideaId={ideaId}
                        expandedMetrics={expandedMetrics}
                        expandedDetails={expandedDetails}
                        expandedSimulations={expandedSimulations}
                        onToggleMetrics={(draftId) => setExpandedMetrics(prev => ({ ...prev, [draftId]: !expandedMetrics[draftId] }))}
                        onToggleDetails={(draftId) => setExpandedDetails(prev => ({ ...prev, [draftId]: !expandedDetails[draftId] }))}
                        onToggleSimulations={(draftId) => setExpandedSimulations(prev => ({ ...prev, [draftId]: !expandedSimulations[draftId] }))}
                        onCopyDraft={handleCopyDraft}
                        getImpactColor={getImpactColor}
                        getBaselineDeltaWord={getBaselineDeltaWord}
                        getInitialSimulationLimit={getInitialSimulationLimit}
                      />
                    ))}
                </Stack>
              </Box>
            )}
          </Paper>
        )}
      </Stack>
    </>
  );
};

export default IdeaResultsContainer;
